import React, { useMemo, useEffect, useContext, useCallback } from 'react';

import { updateTask } from 'actions/tasks';

import { createAlert } from 'services/alert-service';
import { getTaskStart, getTaskEnd } from 'utils/task';

import ProjectContainer from 'containers/project-container';

import TimelineTask from 'components/TimelineTask';

import { GanttChartContext } from 'context/GanttChartContext';

import './TimelineTask.styles.css';

const getTaskStartEnd = task => ({
    start: getTaskStart({
        startDate: task.start_date,
        dueDate: task.due_date,
        completedOn: task.completed_on,
        storyPoints: task.story_points
    }),
    end: getTaskEnd({
        startDate: task.start_date,
        dueDate: task.due_date,
        completedOn: task.completed_on,
        storyPoints: task.story_points
    })
});

const tasksCollide = (task, otherTasks) => {
    if (!otherTasks.length) {
        return false;
    }

    const { start, end } = getTaskStartEnd(task);

    return otherTasks.some(otherTask => {
        const otherTaskStartEnd = getTaskStartEnd(otherTask);

        if (end <= otherTaskStartEnd.start || start >= otherTaskStartEnd.end) {
            return false;
        }

        return true;
    });
}

const TimelineTasks = ({ milestoneStart, tasks, excludedUser, rowHeight, showProject, draggable, overlap = true, withGutter, boundToParent, onRowCountChange, onTaskClick }) => {
    const ganttChartContext = useContext(GanttChartContext);

    const rows = useMemo(saveRows => {
        const result = [];

        tasks.forEach(task => {
            let rowToInsert = !overlap ? null : result.find(row => !tasksCollide(task, row));

            if (!rowToInsert) {
                rowToInsert = [];
                result.push(rowToInsert);
            }

            rowToInsert.push(task);
        });

        return result;
    }, [tasks, overlap]);

    const rowsCount = useMemo(() => rows.length, [rows]);

    const style = useMemo(() => ({ height: rowsCount * rowHeight + (withGutter ? 20 : 0) }), [rowsCount, rowHeight, withGutter]);

    useEffect(() => {
        onRowCountChange && onRowCountChange(rowsCount);
    }, [rowsCount, onRowCountChange]);

    return (
        <div className={'timeline-tasks-inner' + (withGutter ? ' with-gutter' : '')} style={style}>
            {
                tasks.map(task =>
                    <TimelineTask
                        key={task.id} 
                        taskId={task.id}
                        row={rows.findIndex(row => row.includes(task))}
                        taskName={task.name}
                        completed={task.completed}
                        rowHeight={rowHeight}
                        dayWidth={ganttChartContext.dayWidth}
                        chartStart={milestoneStart || ganttChartContext.chartStart}
                        projectId={task.project}
                        taskGroupId={task.task_group}
                        dueDate={task.due_date}
                        startDate={task.start_date}
                        completedOn={task.completed_on}
                        storyPoints={task.story_points}
                        excludedUser={excludedUser}
                        assignees={task.assignees}
                        showProject={showProject}
                        draggable={draggable}
                        boundToParent={boundToParent}
                        onClick={onTaskClick}
                    />
                )
            }
        </div>
    )
}

export default TimelineTasks;
